import React, { useMemo, useState } from 'react';
import { Box, IconButton, TextField, Typography, Tooltip } from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon, Edit as EditIcon, Done as DoneIcon } from '@mui/icons-material';
import { FieldEntryDTO, FieldType, SectionFieldDTO } from '../magic-report.type';
import { SelectFieldRenderer } from './field-renderers/SelectFieldRenderer';
import { DOCUMENTS_RECEIVED_STATUS_OPTIONS } from './sections.config';

const DOC_TYPES_FIELD = 'DocumentsReceivedDocTypes';
const DEFAULT_MAX_DOC_TYPES = 18;

interface DocumentTypeConfig {
  fieldName: string;
  label: string;
  isCustom?: boolean;
}

interface DocumentsReceivedTableProps {
  docTypesField: SectionFieldDTO | undefined;
  handleFieldChange: (fieldName: string, updates: Partial<SectionFieldDTO>) => void;
  getField: (fieldName: string) => SectionFieldDTO | undefined;
  maxDocTypes?: number;
}

const parseDocType = (value: string): DocumentTypeConfig | null => {
  if (!value) return null;
  try {
    const parsed = JSON.parse(value);
    if (parsed && typeof parsed.fieldName === 'string' && typeof parsed.label === 'string') {
      return parsed;
    }
    return null;
  } catch {
    return null;
  }
};

const serializeDocType = (doc: DocumentTypeConfig): string => JSON.stringify(doc);

export const DocumentsReceivedTable: React.FC<DocumentsReceivedTableProps> = ({
  docTypesField,
  handleFieldChange,
  getField,
  maxDocTypes = DEFAULT_MAX_DOC_TYPES,
}) => {
  const [isEditMode, setIsEditMode] = useState(false);
  const [newDocLabel, setNewDocLabel] = useState('');

  const docTypeEntries = docTypesField?.entries || [];

  const docTypes: DocumentTypeConfig[] = useMemo(
    () => docTypeEntries.map(entry => parseDocType(entry.value || '')).filter((x): x is DocumentTypeConfig => x !== null),
    [docTypeEntries],
  );

  const totalCount = docTypes.length;
  const canAddMore = totalCount < maxDocTypes;

  const updateDocTypes = (updated: DocumentTypeConfig[]) => {
    const entries: FieldEntryDTO[] = updated.map((doc, index) => ({
      value: serializeDocType(doc),
      addToActionItems: false,
      order: index,
    }));

    handleFieldChange(DOC_TYPES_FIELD, { entries });
  };

  const handleLabelChange = (index: number, label: string) => {
    const updated = [...docTypes];
    updated[index] = { ...updated[index], label };
    updateDocTypes(updated);
  };

  const handleDeleteRow = (index: number) => {
    const doc = docTypes[index];
    const updated = docTypes.filter((_, i) => i !== index);
    updateDocTypes(updated);

    // When a document type is removed, set its status to N/A so it no longer
    // affects automated comments that look for "No" values.
    const statusField = getField(doc.fieldName);
    if (statusField) {
      handleFieldChange(doc.fieldName, { value: 'N/A', addToActionItems: false });
    }
  };

  const generateNewFieldName = (): string => {
    const existingNames = new Set(docTypes.map(d => d.fieldName));
    let counter = 1;
    // Ensure we don't collide with any existing field names
    // Use a safe upper bound to avoid an infinite loop
    const maxAttempts = (maxDocTypes || DEFAULT_MAX_DOC_TYPES) * 5;
    while (counter <= maxAttempts) {
      const candidate = `AdditionalDocument${counter}`;
      if (!existingNames.has(candidate)) {
        return candidate;
      }
      counter += 1;
    }
    // Fallback – extremely unlikely to be hit
    return `AdditionalDocument_${Date.now()}`;
  };

  const handleAddDocType = () => {
    const trimmed = newDocLabel.trim();
    if (!trimmed || !canAddMore) return;

    const fieldName = generateNewFieldName();
    const newDoc: DocumentTypeConfig = {
      fieldName,
      label: trimmed,
      isCustom: true,
    };

    const updated = [...docTypes, newDoc];
    updateDocTypes(updated);

    // Initialize status field for the new document type
    handleFieldChange(fieldName, {
      fieldType: FieldType.Select,
      value: 'No',
      addToActionItems: false,
    });

    setNewDocLabel('');
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <Box
        sx={{
          border: '1px solid',
          borderColor: 'grey.200',
          borderRadius: 2,
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: '2.2fr 1.4fr 0.4fr',
            alignItems: 'center',
            bgcolor: 'grey.100',
            px: 2,
            py: 1,
            columnGap: 2,
          }}
        >
          <Typography variant='body2' fontWeight='medium'>
            Document Type
          </Typography>
          <Typography variant='body2' fontWeight='medium' sx={{ textAlign: 'center' }}>
            Received?
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <Tooltip title={isEditMode ? 'Done editing document types' : 'Edit document types'}>
              <IconButton size='small' onClick={() => setIsEditMode(prev => !prev)}>
                {isEditMode ? <DoneIcon fontSize='small' /> : <EditIcon fontSize='small' />}
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {docTypes.map((doc, index) => {
          const statusField = getField(doc.fieldName);

          return (
            <Box
              key={doc.fieldName}
              sx={{
                display: 'grid',
                gridTemplateColumns: '2.2fr 1.4fr 0.4fr',
                alignItems: 'center',
                columnGap: 2,
                px: 2,
                py: 1,
                bgcolor: index % 2 === 0 ? 'grey.50' : 'background.paper',
                borderTop: '1px solid',
                borderColor: 'grey.200',
              }}
            >
              <Box>
                {isEditMode ? (
                  <TextField size='small' fullWidth value={doc.label} onChange={e => handleLabelChange(index, e.target.value)} />
                ) : (
                  <Typography variant='body2'>{doc.label}</Typography>
                )}
              </Box>

              <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                <SelectFieldRenderer
                  value={statusField?.value}
                  addToActionItems={statusField?.addToActionItems}
                  onChange={(value, addToActionItems) => handleFieldChange(doc.fieldName, { value, addToActionItems: !!addToActionItems })}
                  fieldName={doc.fieldName}
                  options={DOCUMENTS_RECEIVED_STATUS_OPTIONS}
                  showBookmark={false}
                />
              </Box>

              <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                {isEditMode ? (
                  <IconButton size='small' onClick={() => handleDeleteRow(index)} sx={{ color: 'error.main' }} disabled={totalCount <= 1}>
                    <DeleteIcon fontSize='small' />
                  </IconButton>
                ) : null}
              </Box>
            </Box>
          );
        })}
      </Box>

      {isEditMode && (
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: 1 }}>
          <TextField
            placeholder='Add additional document type'
            value={newDocLabel}
            onChange={e => setNewDocLabel(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleAddDocType();
              }
            }}
            size='small'
            sx={{ flexGrow: 1 }}
            disabled={!canAddMore}
          />
          <Tooltip title={canAddMore ? 'Add document type' : `Maximum of ${maxDocTypes} document types reached`}>
            <span>
              <IconButton
                onClick={handleAddDocType}
                size='small'
                disabled={!newDocLabel.trim() || !canAddMore}
                sx={{
                  border: '1px dashed',
                  borderColor: 'divider',
                  transition: 'all 0.2s ease-in-out',
                  '&:hover': {
                    borderColor: 'primary.main',
                    color: 'primary.main',
                    transform: 'scale(1.05)',
                  },
                }}
              >
                <AddIcon fontSize='small' />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      )}
    </Box>
  );
};
